/**
 * Hook that reads a `prompt` query parameter from the URL
 * and sends it as a chat message directly.
 * Cleans up the URL param after consumption.
 */

import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

const PROMPT_PARAM = 'prompt'

interface UseChatPromptInjectionParams {
  sendMessage: (content: string, attachmentIdList?: string[]) => void
}

export const useChatPromptInjection = ({ sendMessage }: UseChatPromptInjectionParams): void => {
  const [searchParams, setSearchParams] = useSearchParams()
  const isConsumedRef = useRef(false)

  const prompt = searchParams.get(PROMPT_PARAM)

  useEffect(() => {
    if (!prompt || isConsumedRef.current) {
      return
    }

    isConsumedRef.current = true

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete(PROMPT_PARAM)
        return next
      },
      { replace: true }
    )

    sendMessage(prompt)
  }, [prompt, sendMessage, setSearchParams])
}
